/**
 * Real OAuth clients: each provider returns a token that the backend verifies
 * server-side (`POST /auth/social`). No credential is trusted client-side.
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI || (typeof window !== 'undefined' ? window.location.origin : '');

const scriptPromises = {};

const loadScript = (src) => {
  if (!scriptPromises[src]) {
    scriptPromises[src] = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.defer = true;
      el.onload = resolve;
      el.onerror = () => reject(new Error(`Impossible de charger ${src}`));
      document.head.appendChild(el);
    });
  }
  return scriptPromises[src];
};

const randomNonce = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const signInWithGoogle = async () => {
  if (!GOOGLE_CLIENT_ID) throw new Error('Google non configuré');
  await loadScript('https://accounts.google.com/gsi/client');
  return new Promise((resolve, reject) => {
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      nonce: randomNonce(),
      callback: (response) => {
        if (response?.credential) resolve({ token: response.credential });
        else reject(new Error('Connexion Google annulée'));
      },
    });
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
        reject(new Error('Connexion Google indisponible, autorisez les cookies tiers'));
      }
    });
  });
};

const signInWithFacebook = async () => {
  if (!FACEBOOK_APP_ID) throw new Error('Facebook non configuré');
  await loadScript('https://connect.facebook.net/fr_FR/sdk.js');
  if (!window.FB.__blablacolisInit) {
    window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: false, version: 'v19.0' });
    window.FB.__blablacolisInit = true;
  }
  return new Promise((resolve, reject) => {
    window.FB.login(
      (response) => {
        const token = response?.authResponse?.accessToken;
        if (token) resolve({ token });
        else reject(new Error('Connexion Facebook annulée'));
      },
      { scope: 'public_profile,email' }
    );
  });
};

const signInWithApple = async () => {
  if (!APPLE_CLIENT_ID) throw new Error('Apple non configuré');
  await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/fr_FR/appleid.auth.js');
  window.AppleID.auth.init({
    clientId: APPLE_CLIENT_ID,
    scope: 'name email',
    redirectURI: APPLE_REDIRECT_URI,
    nonce: randomNonce(),
    usePopup: true,
  });
  const response = await window.AppleID.auth.signIn();
  const token = response?.authorization?.id_token;
  if (!token) throw new Error('Connexion Apple annulée');
  const name = response?.user?.name;
  const fullName = name ? [name.firstName, name.lastName].filter(Boolean).join(' ') : undefined;
  return { token, full_name: fullName };
};

const PROVIDERS = {
  google: { available: () => !!GOOGLE_CLIENT_ID, signIn: signInWithGoogle },
  facebook: { available: () => !!FACEBOOK_APP_ID, signIn: signInWithFacebook },
  apple: { available: () => !!APPLE_CLIENT_ID, signIn: signInWithApple },
};

export const isProviderConfigured = (provider) => !!PROVIDERS[provider]?.available();

export const availableProviders = () => Object.keys(PROVIDERS).filter(isProviderConfigured);

export const signInWithProvider = (provider) => {
  const entry = PROVIDERS[provider];
  if (!entry) throw new Error('Fournisseur non supporté');
  return entry.signIn();
};
